import { lazy, type ComponentType } from 'react';
import {
  registerTranslations as registerProcessAnalyzerI18n,
  ns as processAnalyzerNs
} from '@bliks-io/process-analyzer-app/i18n';
import {
  registerTranslations as registerDocumentIntakeI18n,
  ns as documentIntakeNs
} from '@bliks-io/document-intake-app/i18n';
import {
  registerTranslations as registerDocumentCaptureI18n,
  ns as documentCaptureNs
} from '@bliks-io/document-capture-app/i18n';
import {
  registerTranslations as registerSystemPairAnalyzerI18n,
  ns as systemPairAnalyzerNs
} from '@bliks-io/system-pair-analyzer-app/i18n';
import {
  registerTranslations as registerComplianceCheckI18n,
  ns as complianceCheckNs
} from '@bliks-io/compliance-check-app/i18n';
import {
  registerTranslations as registerOmniscientI18n,
  ns as omniscientNs
} from '@bliks-io/omniscient-app/i18n';
import type { LocalizedText } from '@bliks-io/contracts';
import i18next from '@/lib/i18n';
import { resolveAppLabel } from '@/lib/app-registry';

const APP_COMPONENTS: Record<string, ComponentType> = {
  'process-analyzer': lazy(() => import('@bliks-io/process-analyzer-app')),
  'document-intake': lazy(() => import('@bliks-io/document-intake-app')),
  'document-capture': lazy(() => import('@bliks-io/document-capture-app')),
  'system-pair-analyzer': lazy(() => import('@bliks-io/system-pair-analyzer-app')),
  'compliance-check': lazy(() => import('@bliks-io/compliance-check-app')),
  omniscient: lazy(() => import('@bliks-io/omniscient-app'))
};

// i18next needs namespaces registered synchronously before t() can resolve them;
// app bundles are lazy-loaded so we register eagerly here.
const APP_I18N: Record<string, { register: () => void; ns: string }> = {
  'process-analyzer': { register: registerProcessAnalyzerI18n, ns: processAnalyzerNs },
  'document-intake': { register: registerDocumentIntakeI18n, ns: documentIntakeNs },
  'document-capture': { register: registerDocumentCaptureI18n, ns: documentCaptureNs },
  'system-pair-analyzer': { register: registerSystemPairAnalyzerI18n, ns: systemPairAnalyzerNs },
  'compliance-check': { register: registerComplianceCheckI18n, ns: complianceCheckNs },
  omniscient: { register: registerOmniscientI18n, ns: omniscientNs }
};

const registeredApps = new Set<string>();

function registerAppTranslations(appId: string): string | null {
  const entry = APP_I18N[appId];
  if (!entry) return null;
  if (!registeredApps.has(appId)) {
    entry.register();
    registeredApps.add(appId);
  }
  return entry.ns;
}

/** Resolve display name: prefer i18n appName, fall back to manifest label, then raw appId. */
export function resolveAppTitle(
  appId: string,
  fallbackLabel: LocalizedText | undefined,
  language: string
): string {
  const ns = registerAppTranslations(appId);
  if (ns) {
    const translated = i18next.t(`${ns}:appName`, { defaultValue: '' });
    if (translated) return translated;
  }
  return fallbackLabel ? resolveAppLabel(fallbackLabel, language) : appId;
}

export function getAppComponent(appId: string): ComponentType | null {
  return APP_COMPONENTS[appId] ?? null;
}
